import { Box, Button, Checkbox, FormControlLabel } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchInternalQuestionIdApi,
  getLatestRecommendedQuestionApi,
} from '../Pal/api/learners';
import { Question } from '@protos/content_management/content.db_pb';
import {
  ContentAttempt,
  QAttemptResultEnum,
  StudentAssessmentResponse,
} from '@protos/learning_management/lms.db_pb';
import {
  deserify,
  getModelCorrectAnswer,
  modelCorrectToAnswerArray,
  QuestionContainerWithSolution,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { useAppSelector } from '../../reduxStore/reduxHooks';

const DemoPalQuestion = () => {
  const [learnerId, setLearnerId] = useState('');
  const [studentName, setStudentName] = useState('');
  const [geneoQuestion, setGeneoQuestion] = useState<Question | undefined>(
    undefined
  );
  const [enablePolling, setEnablePolling] = useState(false);
  const { externalStudentId } = deserify(useAppSelector((state) => state.pal));

  useEffect(() => {
    if (externalStudentId) {
      setLearnerId(externalStudentId);
      setEnablePolling(false);
    }
  }, [externalStudentId]);

  const userAttemptedAnswer = useMemo(() => {
    if (!geneoQuestion) {
      return undefined;
    }
    const modelCorrect = getModelCorrectAnswer(geneoQuestion);
    const answer = modelCorrectToAnswerArray(modelCorrect);
    return answer.length ? answer : undefined;
  }, [geneoQuestion]);

  const currentResponse = useMemo(() => {
    if (!geneoQuestion?.questionId || !userAttemptedAnswer) {
      return undefined;
    }
    return new StudentAssessmentResponse({
      questionId: geneoQuestion.questionId,
      responses: [
        new ContentAttempt({
          answer: userAttemptedAnswer,
        }),
      ],
      isCorrect: QAttemptResultEnum.RESPONSE_CORRECT,
    });
  }, [geneoQuestion?.questionId, userAttemptedAnswer]);

  const { ContentCommonAPIServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();
  const onSubmit = useCallback(
    async (learnerId: string, studentName: string) => {
      try {
        const recommendation = await getLatestRecommendedQuestionApi(
          learnerId || studentName
        );
        const questionIdReceived = recommendation.question_id;
        if (!questionIdReceived) {
          setGeneoQuestion(undefined);
          return;
        }
        const geneo_question_id = (
          await fetchInternalQuestionIdApi(questionIdReceived)
        )?.question_id;
        if (geneo_question_id) {
          const res =
            await ContentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchQuestions(
              {
                questionIds: [geneo_question_id],
              }
            );
          if (res.data) {
            setGeneoQuestion(res.data.questions[0]);
          } else {
            setGeneoQuestion(undefined);
          }
        } else {
          setGeneoQuestion(undefined);
        }
      } catch (err) {
        console.error(err);
      }
    },
    [ContentCommonAPIServiceV1ClientWithStatusCodeHandler]
  );

  useEffect(() => {
    if (!enablePolling) {
      return;
    }
    void onSubmit(learnerId, studentName);
    const intervalId = setInterval(() => {
      void onSubmit(learnerId, studentName);
    }, 3000);
    return () => clearInterval(intervalId);
  }, [enablePolling, learnerId, studentName, onSubmit]);

  return (
    <Box
      sx={{
        margin: '15px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <div>
        Learner Id:{' '}
        <input
          type="text"
          value={learnerId}
          onChange={(e) => {
            setLearnerId(e.target.value);
            setEnablePolling(false);
          }}
        />
      </div>
      <div>
        Student Name:{' '}
        <input
          type="text"
          value={studentName}
          onChange={(e) => setStudentName(e.target.value)}
        />
      </div>
      <div>
        <Button
          variant="contained"
          color="primary"
          onClick={() => onSubmit(learnerId, studentName)}
        >
          Submit
        </Button>
      </div>
      <FormControlLabel
        control={
          <Checkbox
            checked={enablePolling}
            onChange={(e) => setEnablePolling(e.target.checked)}
          />
        }
        label="Enable polling"
      />
      {geneoQuestion && (
        <div>
          <QuestionContainerWithSolution
            question={geneoQuestion}
            showAnswer={true}
            homeworkAttempt={true}
            showSubmitButton={false}
            userAttemptedAnswer={userAttemptedAnswer}
            currentResponse={currentResponse}
          />
        </div>
      )}
    </Box>
  );
};

export default DemoPalQuestion;
